import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';

import { LegalHeading } from '@/components/legal/LegalHeading';
import { LegalText } from '@/components/legal/LegalText';
import { RouterLink } from '@/components/router/RouterLink';
import { makeMetaTitle } from '@/modules/META';

export const Route = createFileRoute('/_content/nutzungsbedingungen')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  head: () => ({
    meta: [makeMetaTitle(['Nutzungsbedingungen'])],
  }),
});

function RouteComponent() {
  return (
    <Fragment>
      <LegalHeading level="h1" sx={{ marginBlockStart: 0 }}>
        Nutzungsbedingungen für den RadMELDER
      </LegalHeading>

      <LegalText>
        Willkommen bei unserem RadMELDER Baden-Württemberg! Bevor Sie unsere Web-Anwendung nutzen, lesen Sie bitte die
        folgenden Nutzungsbedingungen sorgfältig durch. Mit der Nutzung unserer Anwendung stimmen Sie diesen Bedingungen
        zu.
      </LegalText>

      <LegalText component="ol">
        <li>
          <LegalText fontWeight="bold">Allgemeine Bestimmungen</LegalText>

          <LegalText>
            Der RadMELDER wird vom Verkehrsministerium Baden-Württemberg bereitgestellt. Durch die Nutzung der Anwendung
            erklären Sie sich mit diesen Nutzungsbedingungen einverstanden. Der Dienst richtet sich an Personen mit
            berechtigtem Interesse an der Verbesserung der Radverkehrsinfrastruktur in Baden-Württemberg. Die Nutzung
            ist freiwillig.
          </LegalText>
          <LegalText>
            Die Nutzung des RadMELDERs ist kostenlos. Das Verkehrsministerium Baden-Württemberg behält sich jedoch das
            Recht vor, die Nutzungsbedingungen und den Umfang des Dienstes jederzeit zu ändern.
          </LegalText>

          <LegalText>
            Um alle Funktionen des RadMELDERs optimal zu nutzen, wird die Verwendung eines aktuellen Browsers empfohlen.
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Verarbeitung personenbezogener Daten</LegalText>

          <LegalText>
            Bei der Nutzung des RadMELDERs werden im Zuge der Meldungserstellung folgende Daten verarbeitet:
          </LegalText>
          <LegalText component="ul">
            <li>Meldungsinhalte (Standort, Kategorie, Beschreibung des Mangels)</li>
            <li>
              Hochgeladene Bilder (zur Dokumentation des Mangels;{' '}
              <b>
                bitte beachten Sie, dass auf den Bildern keine Personen oder personenbezogenen Daten wie z.B.
                Kfz-Kennzeichen erkennbar sein sollten
              </b>
              )
            </li>
            <li>E-Mail-Adresse (optional) zur Kontaktaufnahme und Rückmeldung durch die zuständige Behörde</li>
          </LegalText>
          <LegalText>
            Die optionale Angabe einer E-Mail-Adresse erfolgt freiwillig. Ohne eine gültige E-Mail-Adresse kann jedoch
            keine individuelle Rückmeldung zur gemeldeten Mängelmeldung erfolgen.
          </LegalText>
          <LegalText>
            Die von Ihnen bereitgestellten Daten werden ausschließlich zur Bearbeitung, Behebung und Dokumentation des
            gemeldeten Mangels verwendet. Nach Abschluss einer Meldung werden{' '}
            <b>alle Daten (einschließlich hochgeladener Bilder)</b> für die Dauer von <b>zwölf Monaten</b> gespeichert.
            Anschließend werden die Meldedaten auf die grundlegenden Angaben – also Standort, Kategorie und Beschreibung
            des Mangels – reduziert und in ein Archiv überführt. Die Bilder und ggf. die E-Mail-Adresse werden dabei
            gelöscht.
          </LegalText>
          <LegalText>
            In bestimmten Gebieten werden eingegebene Meldungen automatisiert an ein angebundenes Drittsystem
            weitergeleitet, falls die dort zuständige Kommune eine eigene Meldeplattform nutzt und diese an den
            RadMELDER angebunden ist. In diesem Fall wird ein Hinweis unter dem Adressfeld bei der Meldungseingabe
            eingeblendet. Die Verarbeitung der Daten erfolgt dort in Verantwortung des jeweiligen Dritten und liegt
            außerhalb des Verantwortungsbereichs des RadMELDER-Systembetreibers.
          </LegalText>
          <LegalText>
            Weitere Informationen zur Datenverarbeitung finden Sie in unserer{' '}
            <RouterLink to="/datenschutz">Datenschutzerklärung</RouterLink>.
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Pflichten der Nutzer</LegalText>

          <LegalText>
            Sie verpflichten sich, den RadMELDER ausschließlich zur Meldung tatsächlicher Mängel und unter Beachtung
            geltender Gesetze zu nutzen. Es ist insbesondere untersagt, Inhalte hochzuladen, die:
          </LegalText>
          <LegalText component="ul">
            <li>Rechte Dritter (z.B. Urheber-, Marken- oder Persönlichkeitsrechte) verletzen,</li>
            <li>beleidigend, verleumderisch, diskriminierend oder pornografisch sind,</li>
            <li>strafbare oder anderweitig rechtswidrige Inhalte enthalten.</li>
          </LegalText>
          <LegalText>
            Sie sind für die Richtigkeit und Vollständigkeit Ihrer Angaben verantwortlich. Das Verkehrsministerium
            behält sich vor, rechtswidrige oder missbräuchliche Inhalte ohne Vorankündigung zu löschen und in
            schwerwiegenden Fällen rechtliche Schritte einzuleiten.
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Haftungsausschluss</LegalText>

          <LegalText>
            Das Verkehrsministerium Baden-Württemberg übernimmt keine Haftung für die Richtigkeit, Vollständigkeit und
            Aktualität der bereitgestellten Informationen.
          </LegalText>
          <LegalText>
            Das Ministerium haftet nicht für Schäden, die durch die Nutzung der Anwendung oder durch falsche oder
            irreführende Meldungen entstehen.
          </LegalText>
          <LegalText>
            Für Inhalte, die von Nutzern hochgeladen werden, übernimmt das Ministerium keine Verantwortung. Es besteht
            kein Anspruch auf ständige Verfügbarkeit oder unterbrechungsfreie Nutzung der Anwendung. Wartungsarbeiten,
            technische Störungen oder externe Einflüsse können den Dienst temporär einschränken.
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Datenschutz</LegalText>

          <LegalText>
            Die Verarbeitung personenbezogener Daten erfolgt gemäß den geltenden Datenschutzgesetzen. Sie haben das
            Recht, jederzeit Auskunft über die zu Ihrer Person gespeicherten Daten zu verlangen sowie deren Berichtigung
            oder Löschung zu beantragen. Weitere Informationen hierzu finden Sie in unserer{' '}
            <RouterLink to="/datenschutz">Datenschutzerklärung</RouterLink>.
          </LegalText>
        </li>
        <li>
          <LegalText fontWeight="bold">Schlussbestimmungen</LegalText>

          <LegalText>
            Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand für Streitigkeiten aus der Nutzung der
            Plattform ist Stuttgart.
          </LegalText>
          <LegalText>
            Sollten einzelne Bestimmungen dieser Nutzungsbedingungen unwirksam sein oder werden, bleibt die Wirksamkeit
            der übrigen Bestimmungen unberührt.
          </LegalText>
        </li>
      </LegalText>

      <br />
      <br />

      <LegalText fontSize="lg" fontWeight="bold">
        Vielen Dank für die Nutzung unseres RadMELDERs!
      </LegalText>
    </Fragment>
  );
}
